import { onPageIn, onMount, onDestroy } from '@/modules/_'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  function initializeSocialShare() {
    // Get the title text from the blog post
    function getShareText(): string {
      const titleElement = document.querySelector(
        '.news_open_title_text'
      ) as HTMLElement | null
      return titleElement?.textContent?.trim() || ''
    }

    // Get current page URL
    function getPageUrl(): string {
      return window.location.href
    }

    // Generate share URLs for each platform
    function generateShareUrls(
      text: string,
      url: string
    ): Record<string, string> {
      const encodedText = encodeURIComponent(text)
      const encodedUrl = encodeURIComponent(url)

      return {
        twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      }
    }

    // Create and show toast notification
    function showToast(message: string): void {
      // Remove any existing toast
      const existingToast = document.querySelector('.copy-toast')
      if (existingToast) {
        existingToast.remove()
      }

      // Create toast element
      const toast = document.createElement('div')
      toast.className = 'copy-toast'
      toast.textContent = message

      // Add styles
      Object.assign(toast.style, {
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%) translateY(100%)',
        backgroundColor: '#333',
        color: 'white',
        padding: '12px 24px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        zIndex: '9999',
        opacity: '0',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      })

      document.body.appendChild(toast)

      // Trigger slide up animation
      requestAnimationFrame(() => {
        toast.style.transform = 'translateX(-50%) translateY(0)'
        toast.style.opacity = '1'
      })

      // Auto-hide after 3 seconds
      setTimeout(() => {
        if (toast.parentNode) {
          toast.style.transform = 'translateX(-50%) translateY(100%)'
          toast.style.opacity = '0'

          setTimeout(() => {
            if (toast.parentNode) {
              toast.remove()
            }
          }, 300)
        }
      }, 3000)
    }

    // Copy link to clipboard function
    async function copyLinkToClipboard(): Promise<void> {
      const pageUrl = getPageUrl()

      try {
        await navigator.clipboard.writeText(pageUrl)
        console.log('Link copied to clipboard!')
        showToast('Link copied to clipboard!')
      } catch (error) {
        console.error('Failed to copy link:', error)
        const success = fallbackCopyToClipboard(pageUrl)
        if (success) {
          showToast('Link copied to clipboard!')
        } else {
          showToast('Failed to copy link')
        }
      }
    }

    // Fallback copy method for older browsers
    function fallbackCopyToClipboard(text: string): boolean {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      let success = false
      try {
        success = document.execCommand('copy')
      } catch (error) {
        console.error('Fallback copy failed:', error)
      }

      document.body.removeChild(textArea)
      return success
    }

    // Main share function
    function handleSocialShare(platform: string): void {
      const shareText = getShareText()
      const pageUrl = getPageUrl()
      const shareUrls = generateShareUrls(shareText, pageUrl)

      if (shareUrls[platform]) {
        window.open(shareUrls[platform], '_blank', 'width=600,height=400')
      }
    }

    // Event listener for social media buttons
    function handleClick(event: Event): void {
      console.log(event)
      const target = event.target as HTMLElement | null
      const mediaElement = target?.closest('[data-media]') as HTMLElement | null
      const mediaType = mediaElement?.getAttribute('data-media')
      console.log(mediaType)

      if (mediaType) {
        event.preventDefault()

        if (mediaType === 'link') {
          copyLinkToClipboard()
        } else if (['twitter', 'facebook', 'linkedin'].includes(mediaType)) {
          handleSocialShare(mediaType)
        }
      }
    }

    // Add event listener
    document.addEventListener('click', handleClick)

    // Return cleanup function
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }

  let cleanup: (() => void) | null = null

  onMount(() => {
    cleanup = initializeSocialShare()
  })

  onDestroy(() => {
    if (cleanup) {
      cleanup()
    }
  })
}
